import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import './renewalBanner.scss';
import { useGetRenewalEligibilityQuery } from '../../../data/renewal';
import { SLICES } from '../../../constants/slices';
import { getBanners } from './banners';
import Banner from '../../banner/banner';
import { useGetBannerContentQuery, useGetCmsTagsQuery } from '../../../data/kontentAi';
import { FeatureFlagContext } from '../../../contextProviders/featureFlagContext';
import { BrandContext } from '../../../contextProviders/brandContext';
import { getGtmService } from '../../../services/gtmService/GtmService';

const RenewalBanner = ({ type = 'inline', policyNumber = undefined, location = 'dashboard' }) => {
  const navigate = useNavigate();
  const { activeBrandContext } = useContext(BrandContext);
  const { cspConfiguration } = useContext(FeatureFlagContext);
  const isKontentAiPreviewEnabled = cspConfiguration?.IsKontentAIPreviewEnabled || false;

  const { authDetails } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);
  const { auth0AccessToken } = authDetails;

  const {
    data: renewalEligibilityData,
    error: renewalEligibilityError,
    isLoading: renewalEligibilityIsLoading,
    isError: renewalEligibilityIsError
  } = useGetRenewalEligibilityQuery(auth0AccessToken, { skip: !auth0AccessToken, refetchOnMountOrArgChange: true });
  const hasEligibleRenewal =
    Array.isArray(renewalEligibilityData) && renewalEligibilityData.some((item) => item.IsRenewalEligible);
  const renewalEligibilityIsLoadingOrError = renewalEligibilityIsLoading || renewalEligibilityIsError;
  const skipBanner = renewalEligibilityIsLoadingOrError || !hasEligibleRenewal;

  const {
    data: bannerContentData,
    isLoading: bannerContentIsLoading,
    isError: bannerContentIsError
  } = useGetBannerContentQuery(
    {
      previewEnabled: isKontentAiPreviewEnabled
    },
    { skip: skipBanner }
  );
  const bannerContentIsLoadingOrError = bannerContentIsLoading || bannerContentIsError;

  const {
    data: cmsTagsData,
    isLoading: cmsTagsIsLoading,
    isError: cmsTagsIsError
  } = useGetCmsTagsQuery(
    {
      brandPartner: activeBrandContext.brand.key,
      previewEnabled: isKontentAiPreviewEnabled
    },
    { skip: skipBanner }
  );
  const cmsTagsIsLoadingOrError = cmsTagsIsLoading || cmsTagsIsError;

  // Computed above the early-return guard below so the impression effect (a hook) can also sit
  // above it — hooks must run unconditionally, in the same order, on every render.
  const shouldRenderBanners = !skipBanner && !bannerContentIsLoadingOrError && !cmsTagsIsLoadingOrError;

  const filteredRenewalData = useMemo(
    () =>
      shouldRenderBanners
        ? renewalEligibilityData
            ?.filter((item) => item.IsRenewalEligible)
            ?.filter((item) => !policyNumber || item.PolicyNumber === policyNumber)
        : undefined,
    [shouldRenderBanners, renewalEligibilityData, policyNumber]
  );

  // Memoised because getBanners runs RichTextParser per banner, and because an unstable identity
  // would re-run the impression effect on every render.
  const banners = useMemo(
    () =>
      shouldRenderBanners ? getBanners(filteredRenewalData, bannerContentData, cmsTagsData, !!policyNumber) : undefined,
    [shouldRenderBanners, filteredRenewalData, bannerContentData, cmsTagsData, policyNumber]
  );

  // Guarded by policy number so one banner yields one impression per page view, not one per
  // RTK Query refetch or parent re-render.
  const impressedRef = useRef(new Set());
  useEffect(() => {
    banners?.forEach((banner) => {
      if (impressedRef.current.has(banner.policyNumber)) return;
      impressedRef.current.add(banner.policyNumber);
      getGtmService().pushRenewalBannerImpression({
        policyNumber: banner.policyNumber,
        bannerLocation: location,
        bannerVariant: banner.renewalStatus,
        bannerCtaLabel: banner.actionLabel,
        canRenew: banner.canRenew,
        daysToExpiry: banner.daysToExpiry
      });
    });
  }, [banners, location]);

  if (!shouldRenderBanners) return null;

  const handleClick = (banner) => {
    getGtmService().pushRenewalBannerClick({
      policyNumber: banner.policyNumber,
      bannerLocation: location,
      bannerVariant: banner.renewalStatus,
      bannerCtaLabel: banner.actionLabel,
      destinationPage: banner.canRenew ? 'adjust_cover' : 'my_pets'
    });

    navigate(banner.redirectUrl, {
      replace: true,
      state: { source: 'banner', sourcePage: location }
    });
  };

  return (
    <div className="bannersList">
      {banners?.map((banner) => (
        <Banner
          key={`banner-${banner.policyNumber}`}
          {...banner}
          onAction={() => handleClick(banner)}
          className={`${type}Banner`}
          actionButtonClassName="compact outline"
        />
      ))}
    </div>
  );
};

RenewalBanner.propTypes = {
  type: PropTypes.oneOf(['full', 'inline']),
  policyNumber: PropTypes.string,
  location: PropTypes.oneOf(['dashboard', 'my_pets'])
};

export default RenewalBanner;
